import React, { Component } from 'react'
import { connect } from 'react-redux'
import formatDate from 'dateformat'
import { Sticky, StickyContainer } from 'react-sticky'

import classNames from './InteractiveHierarchy.css'
import { fetchKeywordGraph, fetchKeywordParents, toggleExpanded, touchNode } from '../redux/keywords'
import { fetchSearchOptions } from '../redux/search'
import LoadingSpinner from './LoadingSpinner'
import ErrorMessage from './ErrorMessage'
import Icon from './Icon'
import { isSuperAdmin } from '../utils/auth'
import { KEYWORD_IGNORE_FILTER_PREFIX } from '../constants'

class KeywordGraphPicker extends Component {
  componentDidMount () {
    const { graph, fetchKeywordGraph, options, fetchSearchOptions } = this.props
    if (this.props.disableFetch) {
      return
    }
    if (!graph.data && !graph.fetching) {
      fetchKeywordGraph()
    }
    if (!options.keywords) {
      fetchSearchOptions()
    }
    if (this.props.enabledKeywords && !this.props.parentMap) {
      this.props.fetchKeywordParents()
    }
  }
  isExpanded (keyword) {
    const { expanded } = this.props
    return expanded.indexOf(keyword) !== -1
  }
  isSelected = node => {
    const { selected, showGrouping } = this.props
    if (selected) {
      if (selected.indexOf(node._id) !== -1 || selected.indexOf(node.id) !== -1) {
        return true
      }
    }
    if (showGrouping) {
      return this.getData(node, 'display__grouped')
    }
  }
  isExcluded = node => {
    const { excluded, showWeighting, showGrouping } = this.props
    if (excluded) {
      if (excluded.indexOf(node._id) !== -1 || excluded.indexOf(node.id) !== -1) {
        return true
      }
    }
    if (showWeighting) {
      return parseInt(this.getData(node, 'discover__weight')) === 0
    }
    if (showGrouping) {
      return this.getData(node, 'display__exclude')
    }
  }
  isDisabled (keyword, expandable, isOld, isNew, isBrandNew) {
    const { enableOld, enableNew, enableAllNew, tags } = this.props
    if (tags) {
      return true
    }
    if (enableOld || enableNew || enableAllNew) {
      if (enableOld && isOld) {
        return false
      }
      if (enableNew && isBrandNew) {
        return false
      }
      if (enableAllNew && isNew) {
        return false
      }
      return true
    }
    const { enabledKeywords, parentMap } = this.props
    if (enabledKeywords) {
      const enabled = enabledKeywords.reduce((array, keyword) => array.concat(parentMap[keyword]), [])
      if (enabled.indexOf(keyword) === -1) {
        return true
      }
    }
    return false
  }
  filterNode = node => {
    const { options, ignoreFilter } = this.props
    if (!node) {
      return false
    }
    if (ignoreFilter) {
      return true
    }
    if (node.name.indexOf(KEYWORD_IGNORE_FILTER_PREFIX) === 0) {
      return true
    }
    return options.keywords.indexOf(node.name) !== -1
  }
  getData (keyword, key, defaultValue) {
    const { value } = this.props
    const combined = {
      ...keyword,
      ...value[keyword._id]
    }
    if (typeof combined[key] === 'undefined') {
      return defaultValue
    }
    return combined[key]
  }
  updateData = (id, key, v) => {
    const { value, onChange } = this.props
    if (!onChange) return
    if (typeof v === 'undefined') {
      onChange({
        ...value,
        [id]: {
          ...value[id],
          ...key
        }
      })
    } else {
      onChange({
        ...value,
        [id]: {
          ...value[id],
          [key]: v
        }
      })
    }
  }
  sortNodes (a, b) {
    return a.name > b.name ? 1 : -1
  }
  isNodeOld = node => {
    return node.updated_at !== this.props.graph.data.updated_at
  }
  isNodeNew = node => {
    return node.created_at === node.updated_at
  }
  isNodeBrandNew = node => {
    return this.isNodeNew(node) && !node.structure__updated_at
  }
  isNodeResolve = node => {
    const { showResolveIndicator } = this.props
    if (!showResolveIndicator) {
      return false
    }
    if (!this.isNodeNew(node)) {
      return false
    }
    return showResolveIndicator(node)
  }
  isNodeHighlighted = node => {
    const { highlightNodes } = this.props
    if (!highlightNodes) {
      return false
    }
    if (highlightNodes.indexOf(node.id) !== -1) {
      return true
    }
    return false
  }
  countChildren = (node, fn) => {
    const { nodes } = this.props
    let count = 0
    for (let i = 0; i !== node.children.length; i++) {
      const id = node.children[i]
      if (!nodes[id]) continue
      if (fn(nodes[id])) {
        count++
      }
      count += this.countChildren(nodes[id], fn)
    }
    return count
  }
  reduceTotalWeight = (weight, id) => {
    const node = typeof id !== 'object' ? this.props.nodes[id] : id
    const nodeWeight = this.getData(node, 'discover__weight', 1)
    return weight + nodeWeight
  }
  renderTree (tree, parent, zeroWeight) {
    const { nodes, toggleExpanded, showWeighting, showGrouping, showTitles, showIndicators } = this.props
    let weightSiblingsParent
    if (showWeighting) {
      weightSiblingsParent = tree.reduce(this.reduceTotalWeight, 0)
      if (parent) {
        weightSiblingsParent += this.getData(parent, 'discover__weight_group', 1)
      }
    }
    const items = tree.map(id => nodes[id]).filter(this.filterNode).sort(this.sortNodes).map(node => {
      const id = node._id
      const keyword = node.name
      const expanded = this.isExpanded(keyword)
      const selected = this.isSelected(node)
      const excluded = this.isExcluded(node)
      const children = node.children.map(id => nodes[id]).filter(this.filterNode)
      const expandable = children.length !== 0
      const isNew = this.isNodeNew(node)
      const isBrandNew = this.isNodeBrandNew(node)
      const isOld = this.isNodeOld(node)
      const isResolve = this.isNodeResolve(node)
      const isHighlighted = this.isNodeHighlighted(node)
      const disabled = this.isDisabled(keyword, expandable, isOld, isNew, isBrandNew)
      const hasOldChildren = !!this.countChildren(node, this.isNodeOld)
      const hasBrandNewChildren = !!this.countChildren(node, this.isNodeBrandNew)
      const hasResolveChildren = !!this.countChildren(node, this.isNodeResolve)
      const hasHighlightedChildren = !!this.countChildren(node, this.isNodeHighlighted)
      const hasSelectedChildren = this.props.showSelectedChildrenIndicator && !!this.countChildren(node, this.isSelected)
      const hasExcludedChildren = this.props.showSelectedChildrenIndicator && !!this.countChildren(node, this.isExcluded)
      let weight
      let weightGroup
      let weightChildren
      if (showWeighting) {
        weight = this.getData(node, 'discover__weight', 1)
        weightGroup = this.getData(node, 'discover__weight_group', 1)
        weightChildren = weightGroup + children.reduce(this.reduceTotalWeight, 0)
      }
      let numberInputClass = classNames.numberInput
      let weightPercent = Math.round(weight / weightSiblingsParent * 100)
      let weightGroupPercent = Math.round(weightGroup / weightChildren * 100)
      if (weight === 0 || zeroWeight) {
        numberInputClass = classNames.numberInputRed
        weightPercent = 0
        weightGroupPercent = 0
      }

      const iconClass = expanded ? classNames.collapseIcon : classNames.icon
      const buttonClass = excluded ? classNames.buttonExcluded : (selected ? classNames.buttonActive : classNames.button)
      const icon = expandable ? (expanded ? 'minus' : 'plus') : 'dot'
      const onToggleExpanded = () => toggleExpanded(keyword)
      const onToggleSelected = () => {
        console.log({ ...node, isOld, isNew, isBrandNew, parent })
        this.props.onSelect && this.props.onSelect({ ...node, isOld, isNew, parent })
        if (showWeighting) {
          this.updateData(id, 'discover__weight', weight === 0 ? 1 : 0)
        } else if (showGrouping) {
          if (selected) {
            this.updateData(id, {
              display__grouped: false,
              display__exclude: true,
              display__title: ''
            })
          } else if (excluded) {
            this.updateData(id, 'display__exclude', false)
          } else {
            this.updateData(id, 'display__grouped', true)
          }
        }
      }
      const onClickSelectAllChildren = () => {
        if (this.props.onSelectAllChildren) {
          if (!expanded) {
            onToggleExpanded()
          }
          this.props.onSelectAllChildren(node.children.map(id => nodes[id]))
        }
      }
      return (
        <li key={keyword} className={classNames.item}>
          <button type='button' onClick={onToggleExpanded} className={iconClass} tabIndex={-1}>
            <Icon icon={icon} size={16} />
          </button>
          {showWeighting &&
            <input
              type='number'
              min={0}
              placeholder='w'
              value={weight}
              className={numberInputClass}
              onChange={e => this.updateData(id, 'discover__weight', e.target.value === '' ? '' : parseInt(e.target.value))}
            />
          }
          {showWeighting &&
            <input
              type='number'
              min={0}
              placeholder='wg'
              value={this.getData(node, 'discover__weight_group', 1)}
              className={numberInputClass}
              onChange={e => this.updateData(id, 'discover__weight_group', e.target.value === '' ? '' : parseInt(e.target.value))}
            />
          }
          {showGrouping &&
            <input
              type='number'
              min={0}
              placeholder='ind'
              value={this.getData(node, 'display__index', '')}
              className={classNames.numberInput}
              onChange={e => this.updateData(id, 'display__index', e.target.value === '' ? '' : parseInt(e.target.value))}
            />
          }
          <button type='button' onClick={onToggleSelected} className={buttonClass} disabled={disabled} tabIndex={-1}>
            {keyword}
            {node.synonyms &&
              <span className={classNames.synonyms}>
                ({node.synonyms.join(', ')})
              </span>
            }
            {node.tags &&
              <span className={classNames.tags}>
                {node.tags.join(', ')}
              </span>
            }
          </button>
          {showTitles && selected &&
            <input
              type='text'
              placeholder='Optional Title'
              value={this.getData(node, 'display__title', '')}
              className={classNames.titleInput}
              onChange={e => this.updateData(id, 'display__title', e.target.value)}
            />
          }
          {showWeighting &&
            <span className={classNames.percent}>{weightPercent}%</span>
          }
          {showWeighting && children.length !== 0 &&
            <span className={classNames.percentGroup}>{weightGroupPercent}%</span>
          }
          {showIndicators && isOld &&
            <span className={classNames.oldAlert}>Deleted or Moved</span>
          }
          {showIndicators && isBrandNew &&
            <span className={classNames.newAlert}>New</span>
          }
          {isResolve &&
            <button className={classNames.resolveAlert} onClick={() => this.props.onClickResolve(node)}>Resolve</button>
          }
          {isHighlighted &&
            <button className={classNames.newAlert}>Selected</button>
          }
          {((showIndicators && hasBrandNewChildren) || hasHighlightedChildren) &&
            <span className={classNames.newAlert} />
          }
          {((showIndicators && hasOldChildren) || hasExcludedChildren) &&
            <span className={classNames.oldAlert} />
          }
          {hasSelectedChildren &&
            <button className={classNames.selectedAlert} />
          }
          {hasResolveChildren &&
            <span className={classNames.resolveAlert} />
          }
          {this.props.onSelectAllChildren && node.children.length > 1 &&
            <button type='button' className={classNames.specialSelect} onClick={onClickSelectAllChildren}>Select Children</button>
          }
          {expanded &&
            this.renderTree(node.children, node, weight === 0 || zeroWeight)
          }
        </li>
      )
    })
    return (
      <ul className={classNames.list}>
        {items}
      </ul>
    )
  }
  render () {
    const { graph, parentMap, options, fetchKeywordGraph, enabledKeywords } = this.props
    if (graph.error) {
      return (
        <ErrorMessage
          message='The keyword graph could not be loaded'
          onRetry={fetchKeywordGraph}
        />
      )
    }
    if (!graph.data || !options.keywords) {
      return <LoadingSpinner />
    }
    if (enabledKeywords && !parentMap) {
      return <LoadingSpinner />
    }
    if (this.props.tags) {
      return this.renderTree(graph.data.tags)
    }
    const totalOld = this.countChildren({ children: graph.data.keywords }, this.isNodeOld)
    const totalNew = this.countChildren({ children: graph.data.keywords }, this.isNodeBrandNew)
    const totalNeedResolve = this.props.showResolveIndicator && this.countChildren({ children: graph.data.keywords }, this.isNodeResolve)
    const showHeader =
      (totalNeedResolve || totalNeedResolve === 0) ||
      this.props.showOldNewCounts ||
      this.props.showOldNewCounts ||
      this.props.headerButtons
    return (
      <StickyContainer>
        {showHeader &&
          <Sticky className={classNames.header} stickyClassName={classNames.headerFloating} stickyStyle={{ top: this.props.stickyBarTop || '0px' }}>
            <ul className={classNames.info}>
              {(totalNeedResolve || totalNeedResolve === 0) &&
                <li>Nodes to resolve: {totalNeedResolve}</li>
              }
              {this.props.showOldNewCounts &&
                <li>Deleted/moved nodes: {totalOld}</li>
              }
              {this.props.showOldNewCounts &&
                <li>New nodes: {totalNew}</li>
              }
            </ul>
            {this.props.headerButtons}
          </Sticky>
        }
        {this.renderTree(graph.data.keywords)}
        <ul className={classNames.info} style={{ marginTop: '20px' }}>
          {this.props.showUpdatedTime &&
            <li>Last updated: {timestampToDate(graph.data[this.props.showUpdatedTime])}</li>
          }
          {this.props.showBackupTime &&
            <li>Last backup: {timestampToDate(graph.data.backup__updated_at)}</li>
          }
          <li>Keyword hierarchy upload from LR: {timestampToDate(graph.data.hierarchy_created_at)}</li>
          <li>Date of the data that was uploaded: {timestampToDate(graph.data.updated_at)}</li>
          {/*
          {totalNeedResolve &&
            <li>Nodes to resolve: {totalNeedResolve}</li>
          }
          {this.props.showOldNewCounts &&
            <li>Deleted/moved nodes: {totalOld}</li>
          }
          {this.props.showOldNewCounts &&
            <li>New nodes: {totalNew}</li>
          }
          */}
          {this.props.showIndicators &&
            <li>
              <button onClick={this.props.fetchKeywordGraph} disabled={graph.fetching} className={classNames.refreshButton}>
                {graph.fetching ? 'Refreshing…' : 'Refresh graph'}
              </button>
            </li>
          }
        </ul>
      </StickyContainer>
    )
  }
}

const timestampToDate = timestamp => timestamp ? formatDate(new Date(timestamp * 1000), 'HH:MM, d mmmm yyyy') : 'Unknown'

export const ExamplePercentage = () => <span className={classNames.percent}>50%</span>
export const ExamplePercentageGroup = () => <span className={classNames.percentGroup}>25%</span>

const mapStateToProps = ({ keywords, search, entities, auth }) => ({
  graph: keywords.graph,
  parentMap: keywords.parents.data,
  options: search.options,
  expanded: keywords.hierarchy.expanded,
  nodes: entities.graph,
  ignoreFilter: isSuperAdmin(auth.user)
})

const mapDispatchToProps = {
  fetchKeywordGraph,
  fetchKeywordParents,
  fetchSearchOptions,
  toggleExpanded,
  touchNode
}

export default connect(mapStateToProps, mapDispatchToProps)(KeywordGraphPicker)



// WEBPACK FOOTER //
// ./src/components/KeywordGraphPicker.js